import { List, Space, Tag, Button, Typography, Avatar, Divider } from 'antd'
import { UserOutlined } from '@ant-design/icons'
import type { ReactNode } from 'react'

const { Text } = Typography

interface ActionableListItemProps {
  title: string | ReactNode
  description?: string | ReactNode
  avatar?: {
    src?: string
    icon?: ReactNode
  }
  metadata?: Array<string | ReactNode>
  tags?: Array<{
    label: string
    color?: string
  }>
  actions?: Array<{
    label: string
    onClick: () => void
    icon?: ReactNode
    type?: 'primary' | 'default' | 'dashed' | 'link'
    danger?: boolean
  }>
  highlight?: boolean
}

/**
 * Actionable List Item Component
 *
 * Card-style list item with clear hierarchy and prominent actions.
 * Replaces dense table rows with scannable, action-oriented layout.
 *
 * Usage:
 * ```tsx
 * <List
 *   dataSource={appointments}
 *   renderItem={(apt) => (
 *     <ActionableListItem
 *       title={apt.patientName}
 *       description={apt.type}
 *       avatar={{ src: apt.patientImage }}
 *       metadata={[
 *         formatDate(apt.scheduledAt),
 *         apt.duration + ' min'
 *       ]}
 *       tags={[{ label: apt.status, color: getStatusColor(apt.status) }]}
 *       actions={[
 *         { label: 'Join Call', onClick: () => {}, type: 'primary' },
 *         { label: 'Reschedule', onClick: () => {} }
 *       ]}
 *       highlight={apt.status === 'IN_PROGRESS'}
 *     />
 *   )}
 * />
 * ```
 */
export function ActionableListItem({
  title,
  description,
  avatar,
  metadata,
  tags,
  actions,
  highlight,
}: ActionableListItemProps) {
  return (
    <List.Item
      actions={
        actions?.map((action, index) => (
          <Button
            key={index}
            type={action.type}
            icon={action.icon}
            onClick={action.onClick}
            danger={action.danger}
          >
            {action.label}
          </Button>
        ))
      }
      style={{
        backgroundColor: highlight ? '#f0f9ff' : undefined,
        borderLeft: highlight ? '4px solid #1890ff' : undefined,
        padding: '16px 24px',
      }}
    >
      <List.Item.Meta
        avatar={
          avatar ? (
            <Avatar
              src={avatar.src}
              icon={avatar.icon || <UserOutlined />}
              size={48}
            />
          ) : undefined
        }
        title={
          <Space size="middle">
            {typeof title === 'string' ? (
              <Text strong style={{ fontSize: 15 }}>
                {title}
              </Text>
            ) : (
              title
            )}
            {tags?.map((tag, index) => (
              <Tag key={index} color={tag.color}>
                {tag.label}
              </Tag>
            ))}
          </Space>
        }
        description={
          <Space direction="vertical" size={4} style={{ marginTop: 4 }}>
            {description && (
              typeof description === 'string' ? (
                <Text type="secondary">{description}</Text>
              ) : (
                description
              )
            )}
            {metadata && metadata.length > 0 && (
              <Space split={<Divider type="vertical" />} size="small">
                {metadata.map((item, index) => (
                  <Text key={index} type="secondary" style={{ fontSize: 13 }}>
                    {item}
                  </Text>
                ))}
              </Space>
            )}
          </Space>
        }
      />
    </List.Item>
  )
}
